import { useMemo } from "react";
import { AttributeDefinitionDc, GeometryType, LayerDefinitionDc } from "@evergis/api";

import { SchemaParsed } from "evergis/core/import/parseSchema";
import { AttributesUpdate, useAttributes } from "hooks/attributes/useAttributes";
import { CalculatedAttributes, useAddCalculatedAttributes } from "hooks/attributes/useAddCalculatedAttributes";
import { createUniqueNameObject } from "../../Aggregation/utils/createUniqueNameObject";
import { ImportAttribute } from "../types";

const useCalculatedAttributes = (attributes: CalculatedAttributes[]) => {
  return useMemo(
    () =>
      attributes.reduce(
        (prev, current) => ({
          ...prev,
          [current.name]: {
            isCalculated: true,
            isDisplayed: true,
            ...current,
          },
        }),
        {},
      ),
    [attributes],
  );
};

type UseImportAttributesConfig = {
  attributes?: SchemaParsed["schemaAttributes"];
  geometryType?: GeometryType;
  geometryAttribute?: string;
  layerDefinitions?: LayerDefinitionDc[];
};

export const useImportAttributes = ({
  attributes,
  geometryType,
  geometryAttribute,
  layerDefinitions,
}: UseImportAttributesConfig): [ImportAttribute[], AttributesUpdate<ImportAttribute>] => {
  const attributesParams = useMemo(
    () => layerDefinitions || [{ geometryType, name: geometryAttribute }],
    [layerDefinitions, geometryType, geometryAttribute],
  );

  const calculatedAttributes = useAddCalculatedAttributes(attributesParams, true);
  const reducedCalculatedAttributes = useCalculatedAttributes(calculatedAttributes);
  const newAttributes: SchemaParsed["schemaAttributes"] = useMemo(
    () =>
      attributes ||
      (Array.isArray(layerDefinitions)
        ? layerDefinitions.reduce((result, layer) => ({ ...result, ...layer.attributes }), {})
        : void 0),
    [attributes, layerDefinitions],
  );
  const importAttributes = useMemo(
    () =>
      newAttributes
        ? {
            ...newAttributes,
            ...Object.keys(reducedCalculatedAttributes).reduce((columns, key) => {
              const currentColumn = reducedCalculatedAttributes[key];
              const columnNames = newAttributes ? Object.keys({ ...newAttributes, ...columns }) : [];
              let column = { [key]: currentColumn };

              if (columnNames.includes(currentColumn.name)) {
                const fixedColumn = createUniqueNameObject(currentColumn, columnNames, "name");

                column = { [fixedColumn.name]: fixedColumn };
              }

              return { ...columns, ...column };
            }, {}),
          }
        : undefined,
    [newAttributes, reducedCalculatedAttributes],
  );
  const createdImportAttributes = useMemo(() => createImportAttributes(importAttributes), [importAttributes]);

  return useAttributes(createdImportAttributes);
};

export const reduceAttributes = (attributes: { [key: string]: AttributeDefinitionDc }): ImportAttribute[] =>
  Object.entries(attributes).map(([key, attributeProps]) => ({
    name: key,
    originalName: key,
    type: attributeProps.type,
    ...attributeProps,
  }));

function createImportAttributes(attributes: SchemaParsed["schemaAttributes"]): ImportAttribute[] | undefined {
  return attributes
    ? [
        ...reduceAttributes(attributes).map((attribute, index) => ({
          ...attribute,
          disabled: false,
          id: `${index}`,
        })),
      ]
    : undefined;
}
